// Floating draggable webcam panel for YNX.
// Provides live preview + snapshot + mood analysis + send-as-image-attachment.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Camera, X, Smile, Image as ImageIcon, Video, RefreshCw, CircleDot } from 'lucide-react';
import { analyzeVision } from '../lib/media';
import { useToast } from './Toast';

const snapshotBase64 = (videoEl) => {
  const canvas = document.createElement('canvas');
  canvas.width = videoEl.videoWidth || 640;
  canvas.height = videoEl.videoHeight || 480;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL('image/png');
  return dataUrl.replace(/^data:image\/png;base64,/, '');
};

export const WebcamPanel = ({ open, onClose, onAttachSnapshot, onAnalysisResult, onSendForVideo, currentModel = 'gpt-4o' }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const toast = useToast();
  const [ready, setReady] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [pos, setPos] = useState({ x: 24, y: window.innerHeight - 340 });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setReady(false);
  }, []);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setReady(true);
    } catch (err) {
      console.warn('YNX: webcam start failed', err);
      toast(`Caméra refusée : ${err.message}`, 'error');
      onClose?.();
    }
  }, [toast, onClose]);

  useEffect(() => {
    if (open) start();
    return () => stop();
  }, [open, start, stop]);

  // Drag handlers
  const onMouseDown = (e) => {
    setDragging(true);
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  };
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const x = Math.max(0, Math.min(window.innerWidth - 360, e.clientX - dragOffset.current.x));
      const y = Math.max(0, Math.min(window.innerHeight - 200, e.clientY - dragOffset.current.y));
      setPos({ x, y });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  if (!open) return null;

  const handleAnalyzeMood = async () => {
    if (!videoRef.current || !ready) return;
    setAnalyzing(true);
    try {
      const b64 = snapshotBase64(videoRef.current);
      const result = await analyzeVision({
        imageBase64: b64,
        question:
          "Tu regardes l'utilisateur via sa webcam. " +
          "1) Décris brièvement ce que tu vois (visage, expression, lumière, contexte). " +
          "2) Estime son humeur du moment. " +
          "3) Donne-lui 2 conseils bienveillants et concrets en français. " +
          "Format court, chaleureux, direct.",
        model: currentModel,
      });
      onAnalysisResult?.(result.text, b64);
      toast('Analyse terminée', 'success');
    } catch (err) {
      console.warn('YNX: mood analysis failed', err);
      toast(`Erreur : ${err.message}`, 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAttach = () => {
    if (!videoRef.current || !ready) return;
    const b64 = snapshotBase64(videoRef.current);
    onAttachSnapshot?.(b64);
    toast('Capture ajoutée en pièce jointe', 'success');
  };

  const handleSendVideo = () => {
    if (!videoRef.current || !ready) return;
    const b64 = snapshotBase64(videoRef.current);
    onSendForVideo?.(b64);
  };

  return (
    <div
      className='webcam-panel'
      style={{ left: pos.x, top: pos.y }}
    >
      <div className='webcam-header' onMouseDown={onMouseDown}>
        <div className='webcam-title'>
          <CircleDot size={10} className='webcam-rec-dot' />
          <span>VOIR · YNX</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className='modal-close' onClick={() => { stop(); start(); }} title='Redémarrer'>
            <RefreshCw size={12} />
          </button>
          <button className='modal-close' onClick={onClose} title='Fermer'>
            <X size={14} />
          </button>
        </div>
      </div>
      <div className='webcam-video-wrap'>
        <video ref={videoRef} className='webcam-video' playsInline muted />
        {!ready && <div className='webcam-loading'>Initialisation caméra…</div>}
      </div>
      <div className='webcam-actions'>
        <button className='hud-btn primary' onClick={handleAnalyzeMood} disabled={!ready || analyzing} title='Analyser mon humeur (vision IA)'>
          {analyzing ? <span className='spin-ring' /> : <Smile size={12} />}
          {analyzing ? ' ANALYSE…' : ' HUMEUR'}
        </button>
        <button className='hud-btn' onClick={handleAttach} disabled={!ready} title='Joindre la capture au prochain message'>
          <ImageIcon size={12} /> JOINDRE
        </button>
        <button className='hud-btn' onClick={handleSendVideo} disabled={!ready} title='Animer cette image en vidéo via Runway'>
          <Video size={12} /> VIDÉO
        </button>
      </div>
    </div>
  );
};
